import { Link } from 'react-router-dom';
import {
  Users,
  Calendar,
  FileText,
  TrendingUp,
  UserPlus,
  CalendarPlus,
  FilePlus,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { StatCard } from '@/components/dashboard/StatCard';
import { AppointmentList } from '@/components/dashboard/AppointmentList';
import { RecentPatients } from '@/components/dashboard/RecentPatients';
import {
  useDashboardStats,
  useTodayAppointments,
  useRecentPatients,
} from '@/hooks/useDashboard';

export function DashboardPage() {
  const { provider, office } = useAuth();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: appointments = [], isLoading: appointmentsLoading } = useTodayAppointments();
  const { data: patients = [], isLoading: patientsLoading } = useRecentPatients();

  // Format date in Icelandic
  const today = new Date();
  const dateStr = today.toLocaleDateString('is-IS', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Format revenue
  const formatRevenue = (amount: number) => {
    return new Intl.NumberFormat('is-IS', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Velkomin, {provider?.name || 'Notandi'}
        </h1>
        <p className="text-gray-600 capitalize">{dateStr}</p>
        {office && (
          <p className="text-sm text-gray-500 mt-2">
            {office.name} • {office.subscription_tier}
          </p>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Heildarfjöldi sjúklinga"
          value={stats?.totalPatients || 0}
          icon={Users}
          loading={statsLoading}
        />
        <StatCard
          title="Tímapantanir í dag"
          value={stats?.todayAppointments || 0}
          icon={Calendar}
          loading={statsLoading}
        />
        <StatCard
          title="Ógreiddir reikningar"
          value={stats?.pendingInvoices || 0}
          icon={FileText}
          loading={statsLoading}
        />
        <StatCard
          title="Tekjur þessa mánaðar"
          value={stats?.monthlyRevenue ? `${formatRevenue(stats.monthlyRevenue)} kr` : '0 kr'}
          icon={TrendingUp}
          loading={statsLoading}
        />
      </div>

      {/* Quick Actions */}
      {provider?.permissions && (
        <div className="bg-white rounded-xl shadow-soft p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Flýtiaðgerðir</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {provider.permissions.edit && (
              <Link
                to="/patients/new"
                className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-brand-primary hover:bg-brand-primary/5 transition-colors group"
              >
                <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-colors">
                  <UserPlus className="w-5 h-5 text-brand-primary group-hover:text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Nýr sjúklingur</p>
                  <p className="text-sm text-gray-600">Skrá nýjan sjúkling</p>
                </div>
              </Link>
            )}

            {provider.permissions.schedule && (
              <Link
                to="/appointments/new"
                className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-brand-primary hover:bg-brand-primary/5 transition-colors group"
              >
                <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-colors">
                  <CalendarPlus className="w-5 h-5 text-brand-primary group-hover:text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Ný tímapöntun</p>
                  <p className="text-sm text-gray-600">Bóka nýjan tíma</p>
                </div>
              </Link>
            )}

            {provider.permissions.billing && (
              <Link
                to="/invoices/new"
                className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-brand-primary hover:bg-brand-primary/5 transition-colors group"
              >
                <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-colors">
                  <FilePlus className="w-5 h-5 text-brand-primary group-hover:text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Nýr reikningur</p>
                  <p className="text-sm text-gray-600">Búa til reikning</p>
                </div>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Today's Appointments and Recent Patients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AppointmentList appointments={appointments} loading={appointmentsLoading} />
        <RecentPatients patients={patients} loading={patientsLoading} />
      </div>
    </div>
  );
}
